import { activeRouteStore, getAttemptedTransitionUrl, setAttemptedTransitionUrl } from '@customerio/hydra-nav-state';
import { useStore } from '@nanostores/react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { Navigate, redirect, RouterProvider, useLocation, useParams, useRouteError } from 'react-router-dom';
import { ConnectedPlumaProvider } from './components/connected-pluma-provider';
import { useCurrentWorkspace } from './context/current-workspace';
import { useAuthQuery } from '@/services/auth';
import { getSampleAccounts } from './features/workspaces/hooks';
import { isEmptyString } from '@/utils/string';
import './fly-components.scss';
import './index.scss';
import Authenticated from './layouts/authenticated';
import EmbedRoot from './layouts/embed-root';
import Root, { queryClient } from './layouts/root';
import { Index } from './pages';
import DesignStudio, { DesignStudioNonBackgroundApp } from './pages/design-studio';
import { ErrorPage } from './pages/error';
import Journeys, { JourneysNonBackgroundApp } from './pages/journeys';
import { NotFoundPage } from './pages/not-found';
import { OAuth2 } from './pages/oauth2';
import Pipelines, { PipelinesNonBackgroundApp } from './pages/pipelines';
import { AppBundle, AppBundleRoutes } from './utils/app-bundles';
import { PageNotFoundError } from './utils/errors';
import injectGlobals from './utils/inject-globals';
import {
  sentryCaptureException,
  sentryCaptureLegacyRedirect,
  sentryCreateBrowserRouter,
  setupSentry,
} from './utils/sentry';
import { AIBusinessProfile } from './pages/journeys/settings/ai-business-profile';
import { WorkspaceAuditLogs as WorkspaceAuditLogsPage } from './pages/journeys/settings/audit-logs';
import AuditLogsPage from './pages/journeys/audit-logs';
import { initializeCrossAppCache } from './utils/cross-app-cache';

export const BOOT_START = performance.now();

setupSentry();
injectGlobals();
initializeCrossAppCache();

interface LastWorkspaceResolverProps {
  appBundle?: AppBundle;
  children: React.ReactNode;
}
function JourneysWithAuthRefresh() {
  // This wraps the Journeys app for routes outside of the /env/ structure. It
  // listens for activeRouteStore changes and redirects to the correct Hydra
  // Journeys embed route when an /env/ URL is detected.
  // This is necessary for login/signup routes to work correctly. In those
  // routes, we embed the Journeys app, which - after login or signup -
  // redirects to the internal /env/ route. When that redirect happens, we need
  // to intercept it and make sure Hydra updates the current route so that we
  // end up in a /journeys/env/ URL.
  const auth = useAuthQuery();

  const location = useLocation();
  const activeRoute = useStore(activeRouteStore);

  useEffect(() => {
    // If we're visiting the login route and there's no redirect URL set (for
    // the journeys UI to redirect post-login), set the redirect to the root URL.
    // This makes sure there's no flash of the nav-less journeys UI after
    // login.
    if (auth.data == null && getAttemptedTransitionUrl() == null && window.location.pathname.startsWith('/login')) {
      setAttemptedTransitionUrl(window.location.origin);
    }
  }, []);

  useEffect(() => {
    // Refresh authentication state, because now the auth cookie is set and we
    // can fetch account/user information from the API
    if (!isEmptyString(activeRoute) && location.pathname !== window.location.pathname) {
      auth.refetch();
    }
  }, [activeRoute, location]);

  // Use the non-background app bundle for e.g signup/login routes, because the
  // background app bundle lives in a different component tree, and this prevents
  // issues with both the background and non-background app bundles being rendered
  // at the same time.
  return <JourneysNonBackgroundApp />;
}

function LastWorkspaceResolver({ appBundle, children }: LastWorkspaceResolverProps) {
  const { workspaceId } = useParams();
  const { pathname, search } = useLocation();
  const currentWorkspace = useCurrentWorkspace();

  const redirectPath = useMemo(() => {
    if (workspaceId !== 'last') {
      return null;
    }

    if (currentWorkspace == null) {
      return null;
    }

    if (appBundle != null) {
      // For app bundles (Pipelines, Design Studio): construct path using AppBundleRoutes
      const appPath = AppBundleRoutes[appBundle];
      // Extract everything after the appPath (e.g., /pipelines/) in the URL
      // Example: if pathname is "/workspaces/123/pipelines/settings"
      // and appPath is "/pipelines", this returns "settings"
      const subPath = pathname.replace(new RegExp(`^.*?${appPath}/`), '');
      return `/workspaces/${currentWorkspace.id}${appPath}/${subPath}${search}`;
    } else {
      // For Hydra-only pages (e.g., AI Business Profile): simple string replacement
      // Example: /workspaces/last/settings/ai-business-profile -> /workspaces/123/settings/ai-business-profile
      return pathname.replace('/workspaces/last', `/workspaces/${currentWorkspace.id}`) + search;
    }
  }, [workspaceId, currentWorkspace?.id, pathname, search, appBundle]);

  if (redirectPath != null) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}
// Redirects old /env/:env_id URLs to new /workspaces/:workspace_id/journeys URLs
const preHydraJourneysEnvRegex = /^\/env\/(last|(\d+))((\/.*$)|$)/;
// Redirects the /journeys/env/env_id URLs (the ones we shipped with for the
// very first production Hydra release) to the new
// /workspaces/:workspace_id/journeys structure
const v1HydraJourneysRegex = /^\/journeys\/env\/(last|(\d+))((\/.*$)|$)/;
const v1HydraPipelinesRegex = /^\/pipelines\/workspaces\/(\d+)((\/.*$)|$)/;
// Redirects from the old /workspaces/:workspace_id/parcel/* URLs to the new
// /workspaces/:workspace_id/design-studio/*
const oldParcelRegex = /^\/workspaces\/(\d+)\/parcel((\/.*$)|$)/;

function RedirectRegexToTargetApp({ regex, targetApp }: { regex: RegExp; targetApp: AppBundle }) {
  let path = `${window.location.pathname}${window.location.search}`;
  const matches = path.match(regex);

  if (matches != null) {
    const [, workspaceId, _, rest] = matches;
    const adjustedRest = rest != null ? rest.replace(/^\//, '') : '';
    path = `/workspaces/${workspaceId}`;

    if (adjustedRest.startsWith('settings')) {
      path = `${path}/${adjustedRest}`;
    } else {
      path = `${path}${AppBundleRoutes[targetApp]}/${adjustedRest}`;
    }
  } else {
    path = `/workspaces${window.location.search}`;
  }

  return <Navigate to={path} replace={true} />;
}

const journeysLegacyUrls = [
  '/logout/*',
  '/reset_password/*',
  '/oauth/*',
  '/integrations/*',
  '/vwb-demo/*',
  '/users/*',
  '/account_canceled/*',
  '/account_cancelled/*',
  '/account/cancelled/*',
  '/post_cancel/*',
];

const journeysLegacyUrlsWithAuthRefresh = [
  '/login/*',
  '/signup/*',
  '/post_registration/*',
  '/confirm_account/*',
  '/account/*',
  '/confirm_account_notice/*',
  '/2fa_setup/*',
  '/no_accessible_workspaces/*',
  '/har-sanitizer/*',
  '/academy-login',
];

function isPageNotFoundError(error: unknown): boolean {
  // our internal 404 error class
  if (error instanceof PageNotFoundError) {
    return true;
  }

  // errors that come from the router
  if (typeof error === 'object' && error != null && 'status' in error && error.status === 404) {
    return true;
  }

  return false;
}

const RootError = () => {
  const error = useRouteError();

  useEffect(() => {
    if (isPageNotFoundError(error)) {
      return;
    }

    sentryCaptureException(error);
  }, [error]);

  if (isPageNotFoundError(error)) {
    return (
      <ConnectedPlumaProvider>
        <NotFoundPage />
      </ConnectedPlumaProvider>
    );
  }

  return (
    <ConnectedPlumaProvider>
      <ErrorPage />
    </ConnectedPlumaProvider>
  );
};

function LegacyRedirectReporter({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const params = useParams();

  // react-router doesn't provide a way to get the route defined in the router (i.e. without params substituted)
  // This reverse-engineers the path based on incoming params to get a more generic string that sentry can combine
  // into a single issue
  const genericPath = Object.entries(params).reduce((acc, [key, value]) => {
    if (value == null) {
      return acc;
    }

    return acc.replace(value, key);
  }, pathname);

  useEffect(() => {
    sentryCaptureLegacyRedirect(genericPath);
  }, []);

  return <>{children}</>;
}

export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <RootError />,
    children: [
      ...journeysLegacyUrls.map((path) => ({
        path,
        element: (
          <LegacyRedirectReporter>
            <JourneysNonBackgroundApp />
          </LegacyRedirectReporter>
        ),
      })),
      ...journeysLegacyUrlsWithAuthRefresh.map((path) => ({
        path,
        element: (
          <LegacyRedirectReporter>
            <JourneysWithAuthRefresh />
          </LegacyRedirectReporter>
        ),
      })),

      // Special route to catch /cdp URLs and render Pipelines.
      // For example, to render the /cdp/oauth/redirect page
      import.meta.env.DEV || import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true'
        ? {
            path: '/cdp/*',
            element: <PipelinesNonBackgroundApp basename="/cdp" />,
          }
        : {},

      {
        path: '/env/*',
        element: (
          <LegacyRedirectReporter>
            <RedirectRegexToTargetApp regex={preHydraJourneysEnvRegex} targetApp={AppBundle.Journeys} />
          </LegacyRedirectReporter>
        ),
      },
      {
        path: '/journeys/*',
        element: <RedirectRegexToTargetApp regex={v1HydraJourneysRegex} targetApp={AppBundle.Journeys} />,
      },
      {
        path: '/pipelines/*',
        element: <RedirectRegexToTargetApp regex={v1HydraPipelinesRegex} targetApp={AppBundle.Pipelines} />,
      },
      {
        id: 'oauth2',
        path: '/oauth2/*',
        element: <OAuth2 />,
      },
      {
        id: 'authenticated',
        element: <Authenticated />,
        children: [
          {
            id: 'sample-workspace',
            path: `/sample-workspace/*`,
            loader: async () => {
              const { workspaceId } = await queryClient.fetchQuery({
                queryKey: ['sampleAccounts'],
                queryFn: getSampleAccounts,
              });

              return redirect(`/workspaces/${workspaceId}/journeys`);
            },
            element: <Journeys />,
          },
          {
            id: 'index',
            element: <Index />,
            index: true,
          },
          {
            id: 'settings_audit_logs',
            path: 'settings/audit-logs',
            element: <AuditLogsPage />,
          },
          {
            id: 'workspace_settings_ai_business_profile',
            path: 'workspaces/:workspaceId/settings/ai-business-profile',
            element: (
              <LastWorkspaceResolver>
                <AIBusinessProfile />
              </LastWorkspaceResolver>
            ),
          },
          {
            id: 'workspace_settings_audit_logs',
            path: 'workspaces/:workspaceId/settings/audit-logs',
            element: (
              <LastWorkspaceResolver>
                <WorkspaceAuditLogsPage />
              </LastWorkspaceResolver>
            ),
          },
          {
            id: 'settings',
            path: 'settings/*',
            element: <Journeys />,
          },
          // Rather than nesting routes (/workspaces, then /:workspaceId under
          // it etc), we use a flat structure to avoid the app fully destroying
          // the Journeys component when switching between settings/workspace
          // settings/internal tools etc.
          // If the routes were nested, the Journeys component would get
          // destroyed and then rerendered, which introduces a few navigation
          // related issues (some transitions not working entirely).
          {
            id: 'workspaces_index',
            path: 'workspaces',
            element: <Index />,
          },
          {
            id: 'workspace_index',
            path: 'workspaces/:workspaceId',
            element: <Index />,
          },
          {
            id: 'workspace_settings',
            path: 'workspaces/:workspaceId/settings/*',
            element: <Journeys />,
          },
          {
            id: 'internal-tools',
            path: `internal-tools/*`,
            element: <Journeys />,
          },
          {
            id: 'trial-expired',
            path: `/trial-expired${AppBundleRoutes[AppBundle.Journeys]}`,
            element: <Journeys />,
          },
          {
            id: AppBundle.Journeys,
            path: `workspaces/:workspaceId${AppBundleRoutes[AppBundle.Journeys]}/*`,
            element: <Journeys />,
          },
          {
            id: AppBundle.Pipelines,
            path: `workspaces/:workspaceId${AppBundleRoutes[AppBundle.Pipelines]}/*`,
            element: (
              <LastWorkspaceResolver appBundle={AppBundle.Pipelines}>
                <Pipelines />
              </LastWorkspaceResolver>
            ),
          },
          // Redirect users from the old parcel app route to the new design studio route
          {
            id: 'parcel',
            path: `workspaces/:workspaceId/parcel/*`,
            element: <RedirectRegexToTargetApp regex={oldParcelRegex} targetApp={AppBundle.DesignStudio} />,
          },
          {
            id: AppBundle.DesignStudio,
            path: `workspaces/:workspaceId${AppBundleRoutes[AppBundle.DesignStudio]}/*`,
            element: (
              <LastWorkspaceResolver appBundle={AppBundle.DesignStudio}>
                <DesignStudio />
              </LastWorkspaceResolver>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/design-studio-embed',
    element: <EmbedRoot />,
    errorElement: <RootError />,
    children: [
      {
        id: 'design-studio-embed-editor',
        path: 'cio/*',
        element: <DesignStudioNonBackgroundApp />,
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement, { identifierPrefix: 'hydra_' }).render(
  <React.StrictMode>
    <NuqsAdapter>
      <RouterProvider router={router} />
    </NuqsAdapter>
  </React.StrictMode>,
);
